import React, { useRef, useState, useEffect, useCallback } from "react";
import { Play, Pause, SkipBack, SkipForward, Plus, X, ListMusic, Repeat, Shuffle } from "lucide-react";
import { toast } from "sonner";
import { useStudio } from "@/studio/StudioContext";
import InfoDot from "@/studio/InfoDot";

function fmt(t) {
  if (!t || isNaN(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function PlaylistPlayer() {
  const { engine, setSelectedChannel } = useStudio();
  const audioRef = useRef(null);
  const connected = useRef(false);

  // When the Player is open, target its channel in the Effects Rack so EQ/FX apply here.
  useEffect(() => {
    setSelectedChannel("player");
  }, [setSelectedChannel]);
  const [tracks, setTracks] = useState([]);
  const [current, setCurrent] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);
  const [loop, setLoop] = useState(false);
  const [shuffle, setShuffle] = useState(false);

  const ensureConnected = useCallback(() => {
    if (connected.current) return;
    try {
      engine.connectMediaElement(audioRef.current);
      connected.current = true;
    } catch (e) {
      /* already connected or context issue */
    }
  }, [engine]);

  const addFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const added = files.map((f) => ({ name: f.name.replace(/\.[^.]+$/, ""), url: URL.createObjectURL(f) }));
    setTracks((prev) => {
      const next = [...prev, ...added];
      if (current === -1) setCurrent(prev.length);
      return next;
    });
    toast.success(`Added ${added.length} track${added.length > 1 ? "s" : ""}`);
    e.target.value = "";
  };

  const playIndex = useCallback(
    (idx) => {
      if (idx < 0 || idx >= tracks.length) return;
      ensureConnected();
      engine.ensureStarted();
      if (engine.ctx.state === "suspended") engine.ctx.resume();
      setCurrent(idx);
      const el = audioRef.current;
      el.src = tracks[idx].url;
      el.play().then(() => setPlaying(true)).catch(() => {});
    },
    [tracks, ensureConnected, engine]
  );

  const togglePlay = () => {
    const el = audioRef.current;
    if (current === -1) {
      if (tracks.length) playIndex(0);
      return;
    }
    ensureConnected();
    engine.ensureStarted();
    if (engine.ctx.state === "suspended") engine.ctx.resume();
    if (el.paused) {
      if (!el.src) el.src = tracks[current].url;
      el.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const nextTrack = useCallback(() => {
    if (!tracks.length) return;
    let idx;
    if (shuffle) idx = Math.floor(Math.random() * tracks.length);
    else idx = (current + 1) % tracks.length;
    playIndex(idx);
  }, [tracks, current, shuffle, playIndex]);

  const prevTrack = () => {
    if (!tracks.length) return;
    const idx = (current - 1 + tracks.length) % tracks.length;
    playIndex(idx);
  };

  const onEnded = useCallback(() => {
    if (loop) {
      playIndex(current);
    } else if (shuffle || current < tracks.length - 1) {
      nextTrack();
    } else {
      setPlaying(false);
    }
  }, [loop, shuffle, current, tracks.length, nextTrack, playIndex]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setTime(el.currentTime);
    const onMeta = () => setDur(el.duration);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("ended", onEnded);
    };
  }, [onEnded]);

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !dur) return;
    el.currentTime = (Number(e.target.value) / 100) * dur;
  };

  const removeTrack = (idx) => {
    setTracks((prev) => prev.filter((_, i) => i !== idx));
    if (idx === current) {
      audioRef.current.pause();
      setPlaying(false);
      setCurrent(-1);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="playlist-player">
      <audio ref={audioRef} crossOrigin="anonymous" />
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#262626] shrink-0">
        <div className="flex items-center gap-2">
          <ListMusic size={14} className="text-[#00e5ff]" />
          <h3 className="font-display text-sm uppercase tracking-widest text-neutral-300 flex items-center gap-1.5">
            Playlist Player
            <InfoDot section="player" label="How the Player works" />
          </h3>
        </div>
        <label
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm border border-[#404040] hover:bg-[#242424] cursor-pointer transition-colors text-[10px] hw-label"
          data-testid="playlist-add-label"
        >
          <Plus size={12} /> Add tracks
          <input
            type="file"
            accept="audio/*"
            multiple
            className="hidden"
            onChange={addFiles}
            data-testid="playlist-add-input"
          />
        </label>
      </div>

      {/* now playing + transport */}
      <div className="px-4 py-3 border-b border-[#262626] shrink-0 bg-[#0d0d0d]">
        <div className="flex items-center justify-between mb-2">
          <span className="font-mono-data text-xs text-[#00e5ff] truncate max-w-[60%]">
            {current >= 0 && tracks[current] ? tracks[current].name : "— nothing queued —"}
          </span>
          <span className="font-mono-data text-[10px] text-neutral-500">
            {fmt(time)} / {fmt(dur)}
          </span>
        </div>
        <input
          data-testid="playlist-seek"
          type="range"
          min={0}
          max={100}
          value={dur ? (time / dur) * 100 : 0}
          onChange={seek}
          className="w-full h-1"
        />
        <div className="flex items-center justify-center gap-3 mt-3">
          <button
            data-testid="playlist-shuffle"
            onClick={() => setShuffle((s) => !s)}
            className="w-8 h-8 rounded-sm flex items-center justify-center transition-colors"
            style={{ color: shuffle ? "#00ff66" : "#737373" }}
          >
            <Shuffle size={15} />
          </button>
          <button
            data-testid="playlist-prev"
            onClick={prevTrack}
            className="w-9 h-9 rounded-sm border border-[#404040] flex items-center justify-center hover:bg-[#242424] transition-colors"
          >
            <SkipBack size={16} className="text-neutral-200" />
          </button>
          <button
            data-testid="playlist-playpause"
            onClick={togglePlay}
            className="w-12 h-12 rounded-full bg-[#00e5ff] flex items-center justify-center hover:bg-cyan-300 transition-colors"
          >
            {playing ? <Pause size={20} className="text-black" fill="black" /> : <Play size={20} className="text-black" fill="black" />}
          </button>
          <button
            data-testid="playlist-next"
            onClick={nextTrack}
            className="w-9 h-9 rounded-sm border border-[#404040] flex items-center justify-center hover:bg-[#242424] transition-colors"
          >
            <SkipForward size={16} className="text-neutral-200" />
          </button>
          <button
            data-testid="playlist-loop"
            onClick={() => setLoop((l) => !l)}
            className="w-8 h-8 rounded-sm flex items-center justify-center transition-colors"
            style={{ color: loop ? "#00ff66" : "#737373" }}
          >
            <Repeat size={15} />
          </button>
        </div>
      </div>

      {/* queue */}
      <div className="flex-1 overflow-y-auto">
        {tracks.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center gap-2 text-neutral-600">
            <ListMusic size={28} />
            <p className="text-xs">Add audio files to build a playlist</p>
          </div>
        )}
        {tracks.map((t, i) => (
          <div
            key={i}
            data-testid={`playlist-item-${i}`}
            onClick={() => playIndex(i)}
            className="flex items-center gap-3 px-4 py-2 border-b border-[#181818] cursor-pointer hover:bg-[#161616] transition-colors group"
            style={i === current ? { background: "rgba(0,229,255,0.08)" } : {}}
          >
            <span className="font-mono-data text-[10px] w-5 text-center" style={{ color: i === current ? "#00e5ff" : "#525252" }}>
              {i === current && playing ? "▶" : i + 1}
            </span>
            <span className="flex-1 text-sm truncate" style={{ color: i === current ? "#00e5ff" : "#d4d4d4" }}>
              {t.name}
            </span>
            <button
              data-testid={`playlist-remove-${i}`}
              onClick={(e) => {
                e.stopPropagation();
                removeTrack(i);
              }}
              className="opacity-0 group-hover:opacity-100 text-neutral-500 hover:text-[#ff3333] transition-all"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
